import { useMemo } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';
import type { NavKey } from '@/lib/navigation-catalog';

/**
 * The tabs of a page that the role may open (navigation matrix): `keys` names the navigation element of each tab value. A tab whose element is OFF is not
 * offered and its content is never mounted. `activeOf` gives the tab to show: the requested one if it is offered, else the first offered one (undefined = none offered).
 */
export function useVisibleTabs<T extends { value: string }>(tabs: readonly T[], keys: Readonly<Record<string, NavKey>>) {
  const { isVisible } = useNavigation();
  return useMemo(() => {
    const visible = tabs.filter((tab) => keys[tab.value] !== undefined && isVisible(keys[tab.value]));
    return {
      tabs: visible,
      has: (value: string) => visible.some((tab) => tab.value === value),
      activeOf: (requested: string): string | undefined => (visible.some((tab) => tab.value === requested) ? requested : visible[0]?.value),
    };
  }, [tabs, keys, isVisible]);
}
